/**
 * Feedback Loop Positioning
 * 
 * Positions nodes that are part of feedback loops (back-edges).
 * Processes each nesting level in order, using previous levels as reference.
 */

import { HORIZONTAL_GAP, PADDING, getNodeDimensions } from './constants';
import { 
  segmentsIntersect, 
  rectsOverlap, 
  createYCalculator,
  getRefPathBounds,
  collectRefPathVectors 
} from './positioningUtils';

/**
 * Position feedback loop nodes level by level.
 * Each level uses the previous level as its reference path.
 */
export function positionFeedbackNodes(positions, feedbackLayerInfo, layerYPositions, graph, nodeDimensions) {
  if (feedbackLayerInfo.size === 0) return;
  
  // Find the maximum nesting level
  let maxLevel = 1;
  for (const [, info] of feedbackLayerInfo) {
    maxLevel = Math.max(maxLevel, info.nestingLevel || 1);
  }
  
  console.log('[Positioning] Feedback nodes:', feedbackLayerInfo.size, '| Max level:', maxLevel);
  
  // Process each level in order
  for (let level = 1; level <= maxLevel; level++) {
    positionFeedbackLevel(level, positions, feedbackLayerInfo, layerYPositions, graph, nodeDimensions);
  }
}

/**
 * Position all feedback nodes at a specific nesting level
 */
function positionFeedbackLevel(level, positions, feedbackLayerInfo, layerYPositions, graph, nodeDimensions) {
  // Get feedback nodes at this level
  const levelNodes = new Map();
  for (const [nodeId, info] of feedbackLayerInfo) {
    if ((info.nestingLevel || 1) === level) {
      levelNodes.set(nodeId, info);
    }
  }
  
  if (levelNodes.size === 0) return;
  
  // Get reference path bounds
  let { refPathMinX, refPathMaxX } = getRefPathBounds(positions, graph, level, nodeDimensions);
  
  // Group nodes by loop index
  const loopGroups = groupNodesByLoop(levelNodes);
  
  // Collect reference path vectors for intersection testing
  const refPathVectors = collectRefPathVectors(positions, graph, level, feedbackLayerInfo, nodeDimensions);
  
  // Debug: Uncomment for detailed logging
  // console.log('[NodePositioning] Level', level, '- reference path X bounds:', refPathMinX, 'to', refPathMaxX);
  // console.log('[NodePositioning] Level', level, '- reference path vectors:', refPathVectors.length);
  
  // Determine which side for each loop
  const loopSides = determineLoopSides(loopGroups, positions, graph, level, refPathMinX, refPathMaxX, refPathVectors, nodeDimensions);
  
  // Calculate max width for this level's feedback nodes
  let maxFeedbackWidth = 0;
  for (const [nodeId] of levelNodes) {
    maxFeedbackWidth = Math.max(maxFeedbackWidth, getNodeDimensions(nodeId, nodeDimensions).width);
  }
  
  // Shift reference path if needed (only for level 1 with left loops)
  const hasLeftLoops = [...loopSides.values()].some(side => side === 'left');
  if (level === 1 && hasLeftLoops) {
    const shiftResult = shiftForLeftLoops(positions, refPathMinX, maxFeedbackWidth);
    refPathMinX = shiftResult.refPathMinX;
    refPathMaxX += shiftResult.shiftAmount;
  }
  
  // Calculate base X positions for this level
  const leftBaseX = refPathMinX - HORIZONTAL_GAP * 2 - maxFeedbackWidth;
  const rightBaseX = refPathMaxX + HORIZONTAL_GAP * 2;
  
  // Debug: Uncomment for detailed logging
  // console.log('[NodePositioning] Level', level, '- leftBaseX:', leftBaseX, 'rightBaseX:', rightBaseX);
  
  // Create Y calculator
  const calculateY = createYCalculator(layerYPositions);
  
  // Position each feedback node
  const placedNodes = [];
  const loopXPositions = new Map();
  const loopLastY = new Map(); // Track the bottom edge of the last placed node in each loop
  
  for (const [nodeId, info] of levelNodes) {
    const { yLayer, loopIndex } = info;
    const loopIdx = loopIndex || 1;
    const side = loopSides.get(loopIdx) || 'right';
    
    const feedbackWidth = getNodeDimensions(nodeId, nodeDimensions).width;
    const feedbackHeight = getNodeDimensions(nodeId, nodeDimensions).height;
    let feedbackY = Math.max(PADDING, calculateY(yLayer));
    
    // Check for vertical overlap with previous node in the same loop
    if (loopLastY.has(loopIdx)) {
      const minY = loopLastY.get(loopIdx) + HORIZONTAL_GAP; // Use HORIZONTAL_GAP as vertical spacing too
      if (feedbackY < minY) {
        feedbackY = minY;
      }
    }
    
    // Calculate X position
    let feedbackX;
    if (loopXPositions.has(loopIdx)) {
      feedbackX = loopXPositions.get(loopIdx);
    } else {
      feedbackX = calculateLoopX(
        side, leftBaseX, rightBaseX, feedbackWidth, feedbackHeight, feedbackY,
        placedNodes, loopIdx
      );
      loopXPositions.set(loopIdx, feedbackX);
    }
    
    positions[nodeId] = { x: feedbackX, y: feedbackY };
    
    // Update the bottom edge for this loop
    loopLastY.set(loopIdx, feedbackY + feedbackHeight);
    
    placedNodes.push({
      id: nodeId,
      x: feedbackX,
      y: feedbackY,
      width: feedbackWidth,
      height: feedbackHeight,
      side: side,
      loopIndex: loopIdx,
    });
    
    // Summary log for each positioned node
    console.log('[Positioning]', nodeId.slice(-8), '→ X:', Math.round(feedbackX), 'Y:', Math.round(feedbackY), 'H:', feedbackHeight, side);
  }
}

/**
 * Group feedback nodes by their loop index
 */
function groupNodesByLoop(levelNodes) {
  const loopGroups = new Map();
  
  for (const [nodeId, info] of levelNodes) {
    const loopIdx = info.loopIndex || 1;
    if (!loopGroups.has(loopIdx)) {
      loopGroups.set(loopIdx, {
        nodes: [],
        targetId: info.targetId,
        targetLayer: info.targetLayer,
        parentLayer: info.parentLayer,
      });
    }
    loopGroups.get(loopIdx).nodes.push({ id: nodeId, info });
  }
  
  return loopGroups;
}

/**
 * Determine which side (left/right) each loop should be placed on
 */
function determineLoopSides(loopGroups, positions, graph, level, refPathMinX, refPathMaxX, refPathVectors, nodeDimensions) {
  const loopSides = new Map();
  
  for (const [loopIdx, loopInfo] of loopGroups) {
    const { targetId, nodes } = loopInfo;
    const targetPos = positions[targetId];
    
    // Find entry parent
    const entryParentId = findEntryParent(nodes, graph, level, positions);
    const entryParentPos = entryParentId ? positions[entryParentId] : null;
    
    if (!targetPos || !entryParentPos) {
      loopSides.set(loopIdx, 'right');
      continue;
    }
    
    // Count intersections for left vs right placement
    const side = chooseSideByIntersections(
      targetId, entryParentId, targetPos, entryParentPos,
      refPathMinX, refPathMaxX, refPathVectors, nodeDimensions
    );
    
    loopSides.set(loopIdx, side);
  }
  
  return loopSides;
}

/**
 * Find the entry parent node for a loop
 */
function findEntryParent(nodes, graph, level, positions) {
  for (const { id: nodeId } of nodes) {
    const allParents = graph.allParents.get(nodeId) || [];
    for (const parentId of allParents) {
      const parentLevel = graph.nodeLevels?.get(parentId) || 0;
      if (parentLevel < level && positions[parentId] && !nodes.some(n => n.id === parentId)) {
        return parentId;
      }
    }
  }
  return null;
}

/**
 * Choose left or right side based on edge intersection counts
 */
function chooseSideByIntersections(targetId, entryParentId, targetPos, entryParentPos, refPathMinX, refPathMaxX, refPathVectors, nodeDimensions) {
  const targetWidth = getNodeDimensions(targetId, nodeDimensions).width;
  const targetHeight = getNodeDimensions(targetId, nodeDimensions).height;
  const entryWidth = getNodeDimensions(entryParentId, nodeDimensions).width;
  const entryHeight = getNodeDimensions(entryParentId, nodeDimensions).height;
  
  const targetCenterY = targetPos.y + targetHeight / 2;
  const entryCenterY = entryParentPos.y + entryHeight / 2;
  const feedbackY = (targetCenterY + entryCenterY) / 2;
  
  const leftFeedbackX = refPathMinX - HORIZONTAL_GAP * 2 - 100;
  const rightFeedbackX = refPathMaxX + HORIZONTAL_GAP * 2 + 100;
  
  const leftEdges = [
    { from: { x: entryParentPos.x, y: entryCenterY }, to: { x: leftFeedbackX, y: feedbackY } },
    { from: { x: leftFeedbackX, y: feedbackY }, to: { x: targetPos.x, y: targetCenterY } },
  ];
  
  const rightEdges = [
    { from: { x: entryParentPos.x + entryWidth, y: entryCenterY }, to: { x: rightFeedbackX, y: feedbackY } },
    { from: { x: rightFeedbackX, y: feedbackY }, to: { x: targetPos.x + targetWidth, y: targetCenterY } },
  ];
  
  let leftIntersections = 0;
  let rightIntersections = 0;
  
  for (const refVector of refPathVectors) {
    if (refVector.fromId === targetId || refVector.toId === targetId ||
        refVector.fromId === entryParentId || refVector.toId === entryParentId) {
      continue;
    }
    
    for (const leftEdge of leftEdges) {
      if (segmentsIntersect(leftEdge.from, leftEdge.to, refVector.from, refVector.to)) {
        leftIntersections++;
      }
    }
    
    for (const rightEdge of rightEdges) {
      if (segmentsIntersect(rightEdge.from, rightEdge.to, refVector.from, refVector.to)) {
        rightIntersections++;
      }
    }
  }
  
  const side = leftIntersections < rightIntersections ? 'left' : 'right';
  
  // Debug: Uncomment for detailed logging
  // console.log('[NodePositioning] Loop - target:', targetId.slice(-8), 'entry:', entryParentId.slice(-8),
  //   '- leftIntersections:', leftIntersections, 'rightIntersections:', rightIntersections,
  //   '-> side:', side);
  
  return side;
}

/**
 * Shift all positioned nodes right to make room for left-side loops
 */
function shiftForLeftLoops(positions, refPathMinX, maxFeedbackWidth) {
  const minLeftSpace = maxFeedbackWidth + HORIZONTAL_GAP * 3;
  let shiftAmount = 0;
  
  if (refPathMinX < PADDING + minLeftSpace) {
    shiftAmount = PADDING + minLeftSpace - refPathMinX;
    
    for (const [nodeId, pos] of Object.entries(positions)) {
      positions[nodeId] = { ...pos, x: pos.x + shiftAmount };
    }
  }
  
  return { refPathMinX: refPathMinX + shiftAmount, shiftAmount };
}

/**
 * Calculate X position for a feedback node, handling overlaps with other loops
 */
function calculateLoopX(side, leftBaseX, rightBaseX, feedbackWidth, feedbackHeight, feedbackY, placedNodes, loopIdx) {
  let feedbackX = side === 'left' ? leftBaseX : rightBaseX;
  
  // Check for overlaps with nodes from OTHER loops on the same side
  const nodeRect = {
    left: feedbackX,
    right: feedbackX + feedbackWidth,
    top: feedbackY,
    bottom: feedbackY + feedbackHeight,
  };
  
  const overlappingNodes = placedNodes.filter(node => {
    if (node.side !== side) return false;
    if (node.loopIndex === loopIdx) return false;
    const existingRect = {
      left: node.x,
      right: node.x + node.width,
      top: node.y,
      bottom: node.y + node.height,
    };
    return rectsOverlap(nodeRect, existingRect);
  });
  
  if (overlappingNodes.length > 0) {
    if (side === 'left') {
      const minX = Math.min(...overlappingNodes.map(n => n.x));
      feedbackX = minX - feedbackWidth - HORIZONTAL_GAP;
    } else {
      const maxX = Math.max(...overlappingNodes.map(n => n.x + n.width));
        feedbackX = maxX + HORIZONTAL_GAP;
      }
    }
  
  return Math.max(PADDING, feedbackX);
}

